// Senkron ön izleme: yeni açılan / kapanan / yeniden açılan / kat değişen mağazalar, seçilerek uygulanır
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Loader2, RefreshCw, PlusCircle, MinusCircle, RotateCcw, Layers, AlertCircle } from 'lucide-react';
import { API, errText } from './storeShared';

const GROUPS = [
  ['new', 'Yeni açılan', PlusCircle, 'text-emerald-700', (x) => x.floor],
  ['closed', 'Kapanan (pasife alınacak)', MinusCircle, 'text-rose-700', (x) => x.floor],
  ['reopened', 'Yeniden açılan', RotateCcw, 'text-sky-700', (x) => x.floor],
  ['floor_changed', 'Kat bilgisi değişen', Layers, 'text-violet-700', (x) => `${x.old || '—'} → ${x.floor}`],
];
const keyOf = (g, x) => `${g}:${x.id || x.name}`;

const Group = ({ sid, g, rows, sel, toggle, setMany }) => {
  const [k, label, Icon, cls, sub] = g;
  if (!rows.length) return null;
  const keys = rows.map(x => `${sid}|${keyOf(k, x)}`);
  const all = keys.every(x => sel.has(x));
  return (
    <div className="rounded-lg border" data-testid={`sync-group-${k}-${sid}`}>
      <label className={`flex items-center gap-2 border-b bg-slate-50 px-3 py-2 text-sm font-semibold ${cls}`}>
        <Checkbox checked={all} onCheckedChange={(v) => setMany(keys, v)} data-testid={`sync-group-all-${k}-${sid}`} /><Icon className="h-4 w-4" />{label} ({rows.length})
      </label>
      <div className="grid max-h-56 gap-x-4 overflow-y-auto p-2 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map(x => {
          const id = `${sid}|${keyOf(k, x)}`;
          return (
            <label key={id} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50">
              <Checkbox checked={sel.has(id)} onCheckedChange={() => toggle(id)} />
              <span className="truncate">{x.name}</span>{sub(x) && <span className="ml-auto shrink-0 text-xs text-slate-400">{sub(x)}</span>}
            </label>
          );
        })}
      </div>
    </div>
  );
};

export const StoreSyncDialog = ({ open, onClose, onApplied }) => {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setData(null);
    try {
      const r = await axios.post(`${API}/stores/sync/preview`);
      setData(r.data);
      setSel(new Set(r.data.flatMap(s => GROUPS.flatMap(([k]) => s[k].map(x => `${s.source.id}|${keyOf(k, x)}`)))));
    } catch (e) { toast.error(errText(e)); setData([]); }
  };
  useEffect(() => { if (open) run(); }, [open]);
  const toggle = (id) => setSel(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const setMany = (ids, v) => setSel(p => { const n = new Set(p); ids.forEach(i => (v ? n.add(i) : n.delete(i))); return n; });
  const changes = (data || []).reduce((n, s) => n + GROUPS.reduce((m, [k]) => m + s[k].length, 0), 0);
  const apply = async () => {
    const items = data.filter(s => !s.error).map(s => {
      const on = (k) => s[k].filter(x => sel.has(`${s.source.id}|${keyOf(k, x)}`));
      return { source_id: s.source.id, add: on('new').map(x => ({ name: x.name, floor: x.floor })), close: on('closed').map(x => x.id), reopen: on('reopened').map(x => x.id), floors: on('floor_changed').map(x => ({ id: x.id, floor: x.floor })) };
    });
    setBusy(true);
    try {
      const r = (await axios.post(`${API}/stores/sync/apply`, { items })).data;
      toast.success(`Senkron tamamlandı: ${r.added} yeni, ${r.closed} kapandı, ${r.reopened} yeniden açıldı, ${r.floors} kat güncellendi`);
      onApplied(); onClose();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="store-sync-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><RefreshCw className="h-5 w-5" />Senkron Ön İzleme</DialogTitle></DialogHeader>
        {data === null ? <p className="flex items-center gap-2 py-10 justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin" />Kaynak sayfalar okunuyor...</p>
          : data.length === 0 ? <p className="py-10 text-center text-slate-500">Henüz kaynak link yok. "Kaynak Linkler" ile ekleyin.</p>
            : (
              <div className="space-y-5">
                {data.map(s => (
                  <div key={s.source.id} className="space-y-2" data-testid={`sync-source-${s.source.id}`}>
                    <p className="text-sm font-semibold text-slate-800">{s.source.label || s.source.url} <span className="font-normal text-slate-400">· {s.fetched} mağaza bulundu · {s.unchanged} değişiklik yok</span></p>
                    {s.error ? <p className="flex items-center gap-2 rounded-lg bg-rose-50 p-3 text-sm text-rose-700"><AlertCircle className="h-4 w-4" />{s.error}</p>
                      : GROUPS.every(([k]) => !s[k].length) ? <p className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">Liste güncel, değişiklik yok.</p>
                        : GROUPS.map(g => <Group key={g[0]} sid={s.source.id} g={g} rows={s[g[0]]} sel={sel} toggle={toggle} setMany={setMany} />)}
                  </div>
                ))}
                <div className="flex items-center justify-end gap-2 border-t pt-3">
                  <span className="mr-auto text-sm text-slate-500">{sel.size} / {changes} değişiklik seçili</span>
                  <Button variant="outline" onClick={run} data-testid="sync-rerun-btn"><RefreshCw className="h-4 w-4 mr-1" />Yeniden Tara</Button>
                  <Button onClick={apply} disabled={busy || !sel.size} className="bg-slate-900 hover:bg-slate-800" data-testid="sync-apply-btn">{busy ? 'Uygulanıyor...' : 'Seçilenleri Uygula'}</Button>
                </div>
              </div>
            )}
      </DialogContent>
    </Dialog>
  );
};
